/**
 * useNavRoutes — selectors cho PrimarySidebar + Breadcrumbs.
 *
 * Doc 29 §6, §8.
 */
import * as React from "react";

import {
  filterByRoles,
  isItemActive,
  NAV_GROUPS,
  type NavGroup,
} from "@/lib/nav-config";
import { useAuthStore } from "@/stores/auth";
import type { RouteName } from "@/stores/route";
import { useRouteStore } from "@/stores/route";

/** Filter NAV_GROUPS theo user roles. Group rỗng sau filter sẽ bị ẩn. */
export function useVisibleNavGroups(): NavGroup[] {
  const user = useAuthStore((s) => s.user);
  const roles = user?.roles ?? [];
  return React.useMemo(
    () =>
      NAV_GROUPS.map((g) => ({ ...g, items: filterByRoles(g.items, roles) }))
        .filter((g) => g.items.length > 0),
    [roles],
  );
}

/** True nếu route hiện tại là admin/settings (cần Breadcrumbs). */
export function useShouldShowBreadcrumbs(): boolean {
  const route = useRouteStore((s) => s.current);
  // Full-screen editors → render header riêng, KHÔNG cần breadcrumbs
  if (route === "admin.forms.edit" || route === "admin.workflows.edit") {
    return false;
  }
  return route !== "chat";
}

/** True nếu chat-specific UI (conversations sidebar, context panel) hiển thị. */
export function useIsChatRoute(): boolean {
  const route = useRouteStore((s) => s.current);
  return route === "chat";
}

/** Active state cho 1 nav item dựa trên route hiện tại. */
export function useIsItemActive(): (item: { route: RouteName; matchPrefixes?: string[] }) => boolean {
  const route = useRouteStore((s) => s.current);
  return React.useCallback(
    (item) => isItemActive(item as never, route),
    [route],
  );
}

// ============================================================================
// Breadcrumbs
// ============================================================================
export interface Breadcrumb {
  label: string;
  /** Nếu undefined → current page, không link. */
  route?: RouteName;
  /** Param cho route (vd code cho admin.forms.edit). */
  param?: string;
}

const BREADCRUMB_MAP: Record<string, (param: string | undefined) => Breadcrumb[]> = {
  admin: () => [{ label: "Dashboard" }],
  "admin.forms": () => [
    { label: "Admin", route: "admin" },
    { label: "Forms" },
  ],
  "admin.forms.edit": (p) => [
    { label: "Admin", route: "admin" },
    { label: "Forms", route: "admin.forms" },
    { label: p ?? "(form)" },
  ],
  "admin.workflows": () => [
    { label: "Admin", route: "admin" },
    { label: "Workflows" },
  ],
  "admin.workflows.edit": (p) => [
    { label: "Admin", route: "admin" },
    { label: "Workflows", route: "admin.workflows" },
    { label: p ?? "(workflow)" },
  ],
  "admin.notifications": () => [
    { label: "Admin", route: "admin" },
    { label: "Notifications" },
  ],
  "admin.translations": () => [
    { label: "Admin", route: "admin" },
    { label: "Translations" },
  ],
  "admin.audit": () => [
    { label: "Admin", route: "admin" },
    { label: "Audit Logs" },
  ],
  "settings": () => [{ label: "Cài đặt" }],
  "settings.personal": () => [{ label: "Cài đặt" }, { label: "Cá nhân" }],
  "settings.notifications": () => [{ label: "Cài đặt" }, { label: "Thông báo" }],
  "settings.overrides": () => [{ label: "Cài đặt" }, { label: "Override field" }],
  "settings.presets": () => [{ label: "Cài đặt" }, { label: "UI Presets" }],
  "settings.password": () => [{ label: "Cài đặt" }, { label: "Đổi mật khẩu" }],
  "settings.tenant": () => [{ label: "Cài đặt" }, { label: "Tenant (Admin)" }],
  "settings.secrets": () => [{ label: "Cài đặt" }, { label: "API Keys (Admin)" }],
};

export function useBreadcrumbs(): Breadcrumb[] {
  const route = useRouteStore((s) => s.current);
  const param = useRouteStore((s) => s.param);
  return React.useMemo(() => {
    const fn = BREADCRUMB_MAP[route];
    return fn ? fn(param) : [];
  }, [route, param]);
}
